import React, { useEffect, useReducer, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useCollection } from "react-firebase-hooks/firestore";
import { ListQueryReducer, initialQuery, getAdvertisers } from "../firebase";
import {
  fetchBanners,
  fetchRetailer,
  fetchPage,
  fetchAdvertisers,
} from "../actions";
import { Grid } from "semantic-ui-react";
import List from "./list";
import ListMenu from "./list-menu";
import Paginator from "../templates/utils/pagination";
import Loading from "../app/loading";
import NotFound from "../app/404";

const Retailer = () => {
  const navigate = useNavigate();
  const { retailer, page } = useParams();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const banners = useSelector((state) => state.banners || []);
  const search = useSelector((state) => state.search);
  const visibleBanners =
    search.type === "bannerType"
      ? banners.filter((banner) => banner.bannerType === search.term)
      : banners;

  useEffect(() => {
    dispatch(fetchRetailer(retailer));
    return () => {
      dispatch(fetchRetailer(null));
    };
  }, [retailer, dispatch]);

  useEffect(() => {
    if (user.restrictByRetailer && user.retailer !== retailer) navigate("/");
  }, [user, retailer, navigate]);

  const [limit, setLimit] = useState(300);
  const [currentRetailer, setCurrentRetailer] = useState(retailer);
  const [start, setStart] = useState(0);
  const [currentPage, setCurrentPage] = useState(page);
  const [pageBy, setPageBy] = useState(10);

  const queryConfig = useRef({
    retailer: retailer,
    advertiser: user.restrictByAdvertiser ? user.advertiser : "",
    limit,
    adnumber: "",
  });

  const [query, setQuery] = useReducer(
    ListQueryReducer,
    initialQuery(queryConfig.current)
  );

  const [values, loading, error] = useCollection(query, {
    snapshotListenOptions: { includeMetadataChanges: true },
  });

  // get banners for retailer
  useEffect(() => {
    if (retailer !== currentRetailer) {
      setQuery({
        type: "retailer",
        payload: initialQuery({ ...queryConfig.current, retailer: retailer }),
      });
      setCurrentRetailer(retailer);
      dispatch(fetchRetailer(retailer));
    }
  }, [retailer, currentRetailer, dispatch]);

  // get banners for search
  useEffect(() => {
    // advertiser
    if (search.type === "advertiser") {
      setQuery({
        ...queryConfig.current,
        type: "search",
        term: search.term,
        search: "advertiser",
        retailer: retailer,
      });
    }
    // adNumber
    if (search.type === "adnumber") {
      setQuery({
        ...queryConfig.current,
        type: "search",
        term: search.term,
        search: "adNumber",
        retailer: retailer,
      });
    }
    return () => {
      setQuery({
        type: "retailer",
        payload: initialQuery({
          retailer: retailer,
          advertiser: user.restrictByAdvertiser ? user.advertiser : "",
          limit,
          adnumber: "",
        }),
      });
    };
  }, [search, retailer, limit, user, setLimit]);

  // dispatch values to banners
  useEffect(() => {
    if (values) {
      dispatch(fetchBanners(values.docs));
    }
    return () => {
      dispatch(fetchBanners([]));
    };
  }, [dispatch, values]);

  // set start property for paging
  useEffect(() => {
    if (parseInt(page) !== currentPage) {
      setStart(parseInt(page) * pageBy - pageBy);
      setCurrentPage(parseInt(page));
      dispatch(fetchPage(page));
    }
  }, [page, currentPage, dispatch]);

  // get advertisers for current retailer
  useEffect(() => {
    if (user) getAdvertisers(retailer, user, dispatch);
    return () => dispatch(fetchAdvertisers([]));
  }, [retailer, user, dispatch]);

  if (error) {
    return <NotFound />;
  }

  return (
    <Grid padded>
      <Grid.Column width={16}>
        {loading ? (
          <Loading />
        ) : (
          <>
            <ListMenu includeSearch />
            <List
              banners={visibleBanners}
              start={start}
              pageBy={pageBy}
              page={page}
            />
            <Paginator
              total={visibleBanners.length}
              page={currentPage}
              pageBy={pageBy}
              setPageBy={setPageBy}
              path={`/${retailer}/list/`}
            />
          </>
        )}
      </Grid.Column>
    </Grid>
  );
};

export default Retailer;
